import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Auth.css';

const ConfirmSignUp: React.FC = () => {
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState('');
  const [isResending, setIsResending] = useState(false);

  const { confirmSignUp, resendConfirmationCode, error: authError, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const email = location.state?.email || '';

  // Redirect to signup if no email provided
  React.useEffect(() => {
    if (!email) {
      navigate('/signup', { replace: true });
    }
  }, [email, navigate]);

  // Clear errors when code changes
  React.useEffect(() => {
    clearError();
    setLocalError('');
  }, [code, clearError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!code.trim()) {
      setLocalError('Verification code is required');
      return;
    }

    setIsLoading(true);
    setLocalError('');
    clearError();

    try {
      await confirmSignUp(email, code.trim());
      navigate('/login', { 
        state: { 
          message: 'Account confirmed successfully! Please sign in.' 
        } 
      });
    } catch (error: any) {
      // Error is handled by AuthContext
      if (!authError) {
        setLocalError(error.message || 'Failed to confirm account');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    setLocalError('');
    clearError();

    try {
      await resendConfirmationCode(email);
      setLocalError(''); // Clear any previous errors
      // Show success message
      const successDiv = document.createElement('div');
      successDiv.className = 'success-message';
      successDiv.textContent = 'Verification code sent! Please check your email.';
      
      const form = document.querySelector('.auth-form');
      const existingSuccess = form?.querySelector('.success-message');
      if (existingSuccess) {
        existingSuccess.remove();
      }
      form?.insertBefore(successDiv, form.firstChild);
      
      // Remove success message after 5 seconds
      setTimeout(() => {
        successDiv.remove();
      }, 5000);
    } catch (error: any) {
      // Error is handled by AuthContext
      if (!authError) {
        setLocalError(error.message || 'Failed to resend code');
      }
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <div className="auth-logo">
            <span className="logo-icon">🎒</span>
            <h1>Simple Packing</h1>
          </div>
          <h2>Confirm Your Account</h2>
          <p>Enter the verification code sent to <strong>{email}</strong></p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {(authError || localError) && (
            <div className="error-message">
              {authError?.message || localError}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="code">Verification Code</label>
            <input
              type="text"
              id="code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              disabled={isLoading}
              placeholder="Enter 6-digit code"
              maxLength={6}
              pattern="[0-9]{6}"
            />
            <small className="form-help">
              Check your email for a 6-digit verification code
            </small>
          </div>

          <button
            type="submit"
            className="auth-button primary"
            disabled={isLoading}
          >
            {isLoading ? 'Confirming...' : 'Confirm Account'}
          </button>

          <button
            type="button"
            className="auth-button secondary"
            onClick={handleResendCode}
            disabled={isResending || isLoading}
          >
            {isResending ? 'Sending...' : 'Resend Code'}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Remember your password?{' '}
            <Link to="/login" className="auth-link">
              Sign in
            </Link>
          </p>
          <p>
            Need to use a different email?{' '}
            <Link to="/signup" className="auth-link">
              Sign up again
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ConfirmSignUp;